
import React from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import UsageChartLegend from './UsageChartLegend';

interface ConsumptionAreaChartProps {
  data: any[];
  hiddenSeries: string[];
  toggleSeriesVisibility: (dataKey: string) => void;
}

const ConsumptionAreaChart: React.FC<ConsumptionAreaChartProps> = ({
  data,
  hiddenSeries,
  toggleSeriesVisibility
}) => {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart
        data={data}
        margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
      >
        <defs>
          <linearGradient id="colorElectricity" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#30D158" stopOpacity={0.8} />
            <stop offset="95%" stopColor="#30D158" stopOpacity={0.2} />
          </linearGradient>
          <linearGradient id="colorGas" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#FF453A" stopOpacity={0.8} />
            <stop offset="95%" stopColor="#FF453A" stopOpacity={0.2} />
          </linearGradient>
          <linearGradient id="colorWater" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#0EA5E9" stopOpacity={0.8} />
            <stop offset="95%" stopColor="#0EA5E9" stopOpacity={0.2} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
        <XAxis 
          dataKey="name"
          tick={{ fontSize: 12 }} 
          axisLine={{ stroke: '#e0e0e0' }}
          tickLine={false}
        />
        <YAxis 
          tick={{ fontSize: 12 }} 
          axisLine={{ stroke: '#e0e0e0' }}
          tickLine={false}
          width={30}
        />
        <Tooltip 
          formatter={(value, name, props) => {
            // Fix the displayed utility name based on dataKey
            let displayName = name;
            let unit = 'kWh';
            
            if (name === 'electricity') {
              displayName = 'Energy';
              unit = 'kWh';
            } else if (name === 'gas') {
              displayName = 'Gas';
              unit = 'm³';
            } else if (name === 'water') {
              displayName = 'Water';
              unit = 'm³';
            }
            
            return [`${value} ${unit}`, displayName];
          }}
          contentStyle={{
            borderRadius: '8px',
            border: 'none',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            backgroundColor: 'rgba(255, 255, 255, 0.97)'
          }}
          labelStyle={{ fontWeight: 'bold', marginBottom: '4px' }}
        />
        <Legend content={(props) => (
          <UsageChartLegend 
            payload={props.payload} 
            hiddenSeries={hiddenSeries}
            onToggleVisibility={toggleSeriesVisibility}
          />
        )} />
        <Area
          type="monotone"
          dataKey="electricity"
          name="Energy"
          stroke="#30D158"
          fillOpacity={1}
          fill="url(#colorElectricity)"
          hide={hiddenSeries.includes('electricity')}
          activeDot={{ r: 6, strokeWidth: 1 }}
          dot={{ r: 3, strokeWidth: 1, fill: "#fff" }}
        />
        <Area
          type="monotone"
          dataKey="gas"
          name="Gas"
          stroke="#FF453A"
          fillOpacity={1}
          fill="url(#colorGas)"
          hide={hiddenSeries.includes('gas')}
          activeDot={{ r: 6, strokeWidth: 1 }}
          dot={{ r: 3, strokeWidth: 1, fill: "#fff" }}
        />
        <Area
          type="monotone"
          dataKey="water"
          name="Water"
          stroke="#0EA5E9"
          fillOpacity={1}
          fill="url(#colorWater)"
          hide={hiddenSeries.includes('water')}
          activeDot={{ r: 6, strokeWidth: 1 }}
          dot={{ r: 3, strokeWidth: 1, fill: "#fff" }}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

export default ConsumptionAreaChart;
